import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import courseService, { CourseType, getPreferredWebinarInstanceId } from '../services/courseService';
import { getFieldsOfStudy } from '../utils/fieldOfStudy';
import type { Course } from '../services/courseService';
import registrationService, { isRegistrationActive } from '../services/registrationService';
import type { Registration, RegistrationStatus } from '../services/registrationService';
import billingService from '../services/billingService';
import type { AddonAccessCheck } from '../services/billingService';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useUserContext } from '../hooks/useUserContext';
import { useAdminSelfAssign } from '../hooks/useAdminSelfAssign';
import { track } from '../services/analytics';
import OnDemandCourseDetail from './OnDemandCourseDetail';
import WebinarCourseDetail from './WebinarCourseDetail';
import ConferenceCourseDetail from './ConferenceCourseDetail';


export default function CourseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status } = useSubscriptionAccess();
  const { isTenantAdmin } = useUserContext();

  const [course, setCourse] = useState<Course | null>(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [courseError, setCourseError] = useState(false);

  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);

  const [regStatus, setRegStatus] = useState<RegistrationStatus | null>(null);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const [courseRegistrations, setCourseRegistrations] = useState<Registration[]>([]);
  const [regLoading, setRegLoading] = useState(true);

  const [addonCheck, setAddonCheck] = useState<AddonAccessCheck | null>(null);
  const [addonCheckLoading, setAddonCheckLoading] = useState(false);
  const [requestUpgradeLoading, setRequestUpgradeLoading] = useState(false);
  const [requestUpgradeSuccess, setRequestUpgradeSuccess] = useState(false);

  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);

  const courseId = Number(id);
  const isDoceboCourse = course?.sourceSystemId === 1;

  useEffect(() => {
    if (!id) return;
    setCourseLoading(true);
    courseService.getCourseById(courseId)
      .then((c) => {
        setCourse(c);
        setCourseLoading(false);
        track('course_viewed', {
          course_id: String(courseId),
          course_type: c.courseType,
        });
      })
      .catch(() => { setCourseError(true); setCourseLoading(false); });
  }, [courseId]);

  useEffect(() => {
    if (
      (course?.courseType === CourseType.Webinar || course?.courseType === CourseType.Conference) &&
      course.upcomingInstances &&
      course.upcomingInstances.length > 0
    ) {
      const paramInstanceId = searchParams.get('instanceId');
      setSelectedInstanceId(
        getPreferredWebinarInstanceId(
          course.upcomingInstances,
          paramInstanceId ? Number(paramInstanceId) : null
        )
      );
    }
  }, [course?.id, course?.courseType, course?.upcomingInstances, searchParams]);

  // Don't pre-select a date when user already has an active registration
  useEffect(() => {
    if (courseRegistrations.some(isRegistrationActive)) {
      setSelectedInstanceId(null);
    }
  }, [courseRegistrations]);

  useEffect(() => {
    if (!id || courseLoading) return;
    let isMounted = true;

    Promise.all([
      registrationService.getRegistrationStatus(courseId),
      registrationService.getUserRegistrations(),
    ])
      .then(([statusResult, registrations]) => {
        if (!isMounted) return;
        setRegStatus(statusResult);
        const forThisCourse = registrations.filter((item) => item.courseId === courseId);
        setCourseRegistrations(forThisCourse);
        setRegistration(
          forThisCourse.find((item) => item.status === 'completed' || isRegistrationActive(item)) ?? null
        );
        setRegLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setRegStatus({ isRegistered: false });
        setRegistration(null);
        setRegLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [courseId, courseLoading, isDoceboCourse]);

  useEffect(() => {
    if (course == null) return;
    courseService.getRelatedCourses({
      id: courseId,
      fieldsOfStudy: getFieldsOfStudy(course),
      level: course.level,
      courseType: course.courseType,
      creditHours: course.creditHours,
    })
      .then(courses => setRecommendedCourses(courses.slice(0, 3)))
      .catch(() => {});
  }, [course, courseId]);

  useEffect(() => {
    if (course == null) return;
    setAddonCheckLoading(true);
    billingService.checkAddonAccess(course.courseType)
      .then((check) => { setAddonCheck(check); setAddonCheckLoading(false); })
      .catch(() => { setAddonCheck({ hasAccess: false, hasPendingRequest: false }); setAddonCheckLoading(false); });
  }, [course?.courseType]);

  const handleRequestUpgrade = async (tenantId: string) => {
    if (!tenantId || !addonCheck?.requiredAddonId) return;
    setRequestUpgradeLoading(true);
    try {
      await billingService.submitAccessRequest(tenantId, addonCheck.requiredAddonId);
      setRequestUpgradeSuccess(true);
      setAddonCheck((prev) => prev ? { ...prev, hasPendingRequest: true } : prev);
    } catch {
      setRequestUpgradeSuccess(true);
      setAddonCheck((prev) => prev ? { ...prev, hasPendingRequest: true } : prev);
    } finally {
      setRequestUpgradeLoading(false);
    }
  };

  const handleRegistrationUpdate = (nextRegistration: Registration) => {
    setRegistration(nextRegistration);
    setRegStatus({
      isRegistered: isRegistrationActive(nextRegistration),
      status: nextRegistration.status,
      registeredAt: nextRegistration.registeredAt,
    });
    setCourseRegistrations((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === nextRegistration.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = nextRegistration;
        return next;
      }
      return [...prev, nextRegistration];
    });
  };

  const handleReconcileRegistrations = async () => {
    try {
      const latestRegistrations = await registrationService.getUserRegistrations();
      const forThisCourse = latestRegistrations.filter((item) => item.courseId === courseId);
      setCourseRegistrations(forThisCourse);
      forThisCourse.forEach((registration) => {
        handleRegistrationUpdate(registration);
      });
    } catch (error) {
      console.warn('Failed to reconcile registration state after operation error', error);
    }
  };

  const onRequestUpgrade = () => {
    const tenantId = status?.subscriptionSource === 'tenant'
      ? status.tenantSubscription?.tenantId
      : undefined;
    if (tenantId) handleRequestUpgrade(tenantId);
  };

  const isAdminWithoutAccess = isTenantAdmin && addonCheck?.isTenantGated === true && !addonCheck?.hasAccess;
  const adminSelfAssign = useAdminSelfAssign(addonCheck?.requiredAddonId, !!isAdminWithoutAccess);

  const handleAdminActivate = async () => {
    await adminSelfAssign.activateSeat();
    if (course) {
      const check = await billingService.checkAddonAccess(course.courseType);
      setAddonCheck(check);
    }
  };

  if (courseLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">Loading course details...</Typography>
      </Box>
    );
  }

  if (courseError || !course) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Course not found
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/app/courses')}>
          Back to Courses
        </Button>
      </Box>
    );
  }

  if (course.courseType === CourseType.Webinar) {
    return (
      <WebinarCourseDetail
        course={course}
        courseRegistrations={courseRegistrations}
        regLoading={regLoading}
        addonCheck={addonCheck}
        addonCheckLoading={addonCheckLoading}
        recommendedCourses={recommendedCourses}
        requestUpgradeLoading={requestUpgradeLoading}
        requestUpgradeSuccess={requestUpgradeSuccess}
        selectedInstanceId={selectedInstanceId}
        onInstanceSelect={setSelectedInstanceId}
        onClearSelection={() => setSelectedInstanceId(null)}
        onRequestUpgrade={onRequestUpgrade}
        onRegistrationUpdate={handleRegistrationUpdate}
        onReconcileRegistrations={handleReconcileRegistrations}
        isTenantAdminProp={isTenantAdmin}
        adminSelfAssign={adminSelfAssign}
        onAdminActivate={handleAdminActivate}
      />
    );
  }

  if (course.courseType === CourseType.Conference) {
    return (
      <ConferenceCourseDetail
        course={course}
        courseRegistrations={courseRegistrations}
        regLoading={regLoading}
        addonCheck={addonCheck}
        addonCheckLoading={addonCheckLoading}
        recommendedCourses={recommendedCourses}
        requestUpgradeLoading={requestUpgradeLoading}
        requestUpgradeSuccess={requestUpgradeSuccess}
        selectedInstanceId={selectedInstanceId}
        onInstanceSelect={setSelectedInstanceId}
        onClearSelection={() => setSelectedInstanceId(null)}
        onRequestUpgrade={onRequestUpgrade}
        onRegistrationUpdate={handleRegistrationUpdate}
        onReconcileRegistrations={handleReconcileRegistrations}
        isTenantAdminProp={isTenantAdmin}
        adminSelfAssign={adminSelfAssign}
        onAdminActivate={handleAdminActivate}
      />
    );
  }

  return (
    <OnDemandCourseDetail
      course={course}
      regStatus={regStatus}
      registration={registration}
      addonCheck={addonCheck}
      recommendedCourses={recommendedCourses}
      requestUpgradeLoading={requestUpgradeLoading}
      requestUpgradeSuccess={requestUpgradeSuccess}
      onRegistrationUpdate={handleRegistrationUpdate}
      onRequestUpgrade={onRequestUpgrade}
      isTenantAdminProp={isTenantAdmin}
      adminSelfAssign={adminSelfAssign}
      onAdminActivate={handleAdminActivate}
    />
  );
}
